import { FC, useState, useRef, useEffect } from "react";
import clsx from "clsx";

import { AdminChatUser } from "../../model/types";

import styles from "./ChatsList.module.scss";

type ChatsListProps = {
  chats: AdminChatUser[];
  selectedUserId?: number | null;
  onSelect: (userId: number) => void;
  onStageChange?: (userId: number, newStage: string) => void;
  isLoading?: boolean;
  className?: string;
  showTitle?: boolean;
};

export const ChatsList: FC<ChatsListProps> = ({
  chats,
  selectedUserId,
  onSelect,
  onStageChange,
  isLoading,
  className,
  showTitle = true,
}) => {
  const [editingStage, setEditingStage] = useState<number | null>(null);
  const [newStage, setNewStage] = useState("");
  const [isCustomStage, setIsCustomStage] = useState(false);
  const stageInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Получаем уникальные стадии из всех чатов
  const availableStages = Array.from(
    new Set(chats.map((chat) => chat.stage).filter(Boolean) as string[])
  ).sort();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        stageInputRef.current &&
        !stageInputRef.current.contains(event.target as Node)
      ) {
        setEditingStage(null);
        setIsCustomStage(false);
        setNewStage("");
      }
    };

    if (editingStage !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [editingStage]);

  const handleStageClick = (
    e: React.MouseEvent,
    userId: number,
    currentStage: string | null | undefined
  ) => {
    e.stopPropagation();
    setEditingStage(userId);
    setNewStage(currentStage || "");
    setIsCustomStage(false);
  };

  const handleStageSelect = (e: React.MouseEvent, stage: string) => {
    e.stopPropagation();
    if (editingStage !== null) {
      setNewStage(stage);
      setIsCustomStage(false);
    }
  };

  const handleCustomStage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCustomStage(true);
    setNewStage("");
    setTimeout(() => {
      stageInputRef.current?.focus();
    }, 0);
  };

  const handleStageSave = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (editingStage !== null && newStage.trim() && onStageChange) {
      onStageChange(editingStage, newStage.trim());
      setEditingStage(null);
      setIsCustomStage(false);
      setNewStage("");
    }
  };

  const handleStageCancel = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingStage(null);
    setIsCustomStage(false);
    setNewStage("");
  };
  if (isLoading) {
    return (
      <div className={clsx(styles.wrapper, className)}>
        <div className={styles.empty}>Загружаем чаты…</div>
      </div>
    );
  }

  if (!chats.length) {
    return (
      <div className={clsx(styles.wrapper, className)}>
        <div className={styles.empty}>Чаты пока не найдены</div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.wrapper, className)}>
      {showTitle && <h3 className={styles.title}>Чаты</h3>}
      <div className={styles.list}>
        {chats.map((chat) => {
          const isActive = chat.user_id === selectedUserId;

          return (
            <button
              key={chat.user_id}
              type="button"
              onClick={() => onSelect(chat.user_id)}
              className={clsx(styles.item, isActive && styles.itemActive)}
            >
              <div className={styles.avatar}>
                {chat.fullname ? chat.fullname.charAt(0).toUpperCase() : "?"}
              </div>
              <div className={styles.meta}>
                <span className={styles.name}>{chat.fullname}</span>
                {chat.username && (
                  <span className={styles.username}>@{chat.username}</span>
                )}
              </div>
              <div className={styles.stage} onClick={(e) => e.stopPropagation()}>
                {editingStage === chat.user_id ? (
                  <div className={styles.stageEditContainer} onClick={(e) => e.stopPropagation()}>
                    {!isCustomStage ? (
                      <div className={styles.stageDropdown} ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                        {availableStages.map((stage) => (
                          <button
                            key={stage}
                            type="button"
                            className={clsx(
                              styles.stageOption,
                              newStage === stage && styles.stageOptionActive
                            )}
                            onClick={(e) => handleStageSelect(e, stage)}
                          >
                            {stage}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={styles.stageOptionCustom}
                          onClick={handleCustomStage}
                        >
                          + Ввести новый статус
                        </button>
                      </div>
                    ) : (
                      <div className={styles.stageInputContainer}>
                        <input
                          ref={stageInputRef}
                          type="text"
                          value={newStage}
                          onChange={(e) => setNewStage(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") {
                              handleStageSave();
                            } else if (e.key === "Escape") {
                              handleStageCancel(e);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Введите новый статус"
                          className={styles.stageInput}
                          autoFocus
                        />
                      </div>
                    )}
                    <div className={styles.stageActions}>
                      <button
                        type="button"
                        className={styles.stageSaveButton}
                        onClick={handleStageSave}
                        disabled={!newStage.trim()}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className={styles.stageCancelButton}
                        onClick={handleStageCancel}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <span
                    className={styles.stageText}
                    onClick={(e) => handleStageClick(e, chat.user_id, chat.stage)}
                    title="Нажмите для изменения статуса"
                  >
                    {chat.stage || "Без статуса"}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

